export type LiquidityWindow = 24 | 48

export type ArtefactFilterReason =
  | 'below_internal_profit'
  | 'insufficient_history'
  | 'below_configured_profit'
  | 'insufficient_liquidity'
  | 'no_recent_sale'
  | 'sale_not_below_median'

export interface ArtefactFilterSettings {
  onlyLiquid: boolean
  liquidityWindow: LiquidityWindow
  minSales: number
  minProfit: number
}

export const DEFAULT_ARTEFACT_FILTER_SETTINGS: ArtefactFilterSettings = {
  onlyLiquid: true,
  liquidityWindow: 48,
  minSales: 10,
  minProfit: 10_000
}

export interface ArtefactFilterCandidate {
  qlt: number
  historySalePrices?: Array<{ time: string | Date }>
  historyCount?: number
  sales24h?: number
  sales48h?: number
  profit?: string | number | null
  rawProfit?: number | null
  minRequiredProfit?: number | null
  salePrice?: string | number | null
  averagePrice?: string | number | null
}

export interface MultiLotStrategyCandidate {
  lotsCount: number
  totalProfit: number
}

export function getCandidateProfit(candidate: ArtefactFilterCandidate): number {
  const value = candidate.rawProfit ?? candidate.profit
  const profit = Number(value)

  return Number.isFinite(profit) ? profit : 0
}

export function getRecentSales(
  candidate: ArtefactFilterCandidate,
  window: LiquidityWindow,
  now = Date.now()
): number {
  const reported = window === 24 ? candidate.sales24h : candidate.sales48h

  if (typeof reported === 'number' && Number.isFinite(reported)) {
    return reported
  }

  const cutoff = now - window * 60 * 60 * 1000

  return (candidate.historySalePrices ?? []).filter(entry => {
    const timestamp = new Date(entry.time).getTime()
    return Number.isFinite(timestamp) && timestamp >= cutoff && timestamp <= now
  }).length
}

export function getArtefactFilterReasons(
  candidate: ArtefactFilterCandidate,
  settings: ArtefactFilterSettings,
  now = Date.now()
): ArtefactFilterReason[] {
  const reasons: ArtefactFilterReason[] = []
  const profit = getCandidateProfit(candidate)
  const internalProfit = Number(candidate.minRequiredProfit ?? 0)
  const historyCount = candidate.historyCount ?? candidate.historySalePrices?.length ?? 0
  const requiredHistory = [3, 4, 5].includes(candidate.qlt) ? 2 : 10

  if (profit < internalProfit) reasons.push('below_internal_profit')
  if (historyCount < requiredHistory) reasons.push('insufficient_history')
  if (profit < settings.minProfit) reasons.push('below_configured_profit')

  if (settings.onlyLiquid) {
    const selectedSales = getRecentSales(candidate, settings.liquidityWindow, now)

    if (selectedSales < settings.minSales) reasons.push('insufficient_liquidity')
    if (settings.liquidityWindow === 48 && getRecentSales(candidate, 24, now) < 1) {
      reasons.push('no_recent_sale')
    }

    const salePrice = Number(candidate.salePrice)
    const averagePrice = Number(candidate.averagePrice)
    if (!salePrice || !averagePrice || salePrice >= averagePrice) {
      reasons.push('sale_not_below_median')
    }
  }

  return reasons
}

export function passesArtefactFilters(
  candidate: ArtefactFilterCandidate,
  settings: ArtefactFilterSettings,
  now = Date.now()
): boolean {
  return getArtefactFilterReasons(candidate, settings, now).length === 0
}

export function getMultiLotStrategyFilterReasons(
  candidate: ArtefactFilterCandidate,
  strategy: MultiLotStrategyCandidate,
  settings: ArtefactFilterSettings,
  now = Date.now()
): ArtefactFilterReason[] {
  const reasons: ArtefactFilterReason[] = []
  const historyCount = candidate.historyCount ?? candidate.historySalePrices?.length ?? 0
  const requiredHistory = [3, 4, 5].includes(candidate.qlt) ? 2 : 10

  if (historyCount < requiredHistory) reasons.push('insufficient_history')
  if (!Number.isFinite(strategy.totalProfit) || strategy.totalProfit < settings.minProfit) {
    reasons.push('below_configured_profit')
  }

  if (settings.onlyLiquid) {
    const selectedSales = getRecentSales(candidate, settings.liquidityWindow, now)
    const requiredSales = Math.max(settings.minSales, strategy.lotsCount)

    if (selectedSales < requiredSales) reasons.push('insufficient_liquidity')
    if (settings.liquidityWindow === 48 && getRecentSales(candidate, 24, now) < 1) {
      reasons.push('no_recent_sale')
    }
  }

  return reasons
}

export function passesMultiLotStrategyFilters(
  candidate: ArtefactFilterCandidate,
  strategy: MultiLotStrategyCandidate,
  settings: ArtefactFilterSettings,
  now = Date.now()
): boolean {
  return getMultiLotStrategyFilterReasons(candidate, strategy, settings, now).length === 0
}

export function getDailySalesRate(
  candidate: ArtefactFilterCandidate,
  window: LiquidityWindow,
  now = Date.now()
): number {
  return getRecentSales(candidate, window, now) / (window / 24)
}
